// frontend/src/lib/supabase.ts
import { createClient, type Session, type SupabaseClient, type User } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Derive the Supabase project ref from the URL for localStorage key construction.
// Supabase stores auth tokens under `sb-{projectRef}-auth-token`.
const supabaseProjectRef = supabaseUrl ? new URL(supabaseUrl).hostname.split('.')[0] : '';
export const SUPABASE_AUTH_TOKEN_KEY = `sb-${supabaseProjectRef}-auth-token`;

/** Our own auth storage key, and the PKCE code-verifier key the SDK derives from
 *  it as `${storageKey}-code-verifier` (auth-js `getCodeChallengeAndMethod`).
 *  Templated rather than written out twice so the pair cannot drift. */
export const AUTH_STORAGE_KEY = 'umd_planner_beta_auth';
export const PKCE_CODE_VERIFIER_KEY = `${AUTH_STORAGE_KEY}-code-verifier`;

if (!supabaseUrl || !supabaseAnonKey) {
  const missingVars: string[] = [];
  if (!supabaseUrl) missingVars.push('REACT_APP_SUPABASE_URL');
  if (!supabaseAnonKey) missingVars.push('REACT_APP_SUPABASE_ANON_KEY');

  console.error(`Missing Supabase environment variables: ${missingVars.join(', ')}`);
  console.error('Please check your .env.local file');

  if (process.env.NODE_ENV === 'development') {
    throw new Error(
      `Missing Supabase environment variables: ${missingVars.join(', ')}\n` +
      'Please create a .env.local file in your frontend directory with:\n' +
      'REACT_APP_SUPABASE_URL=your-supabase-url\n' +
      'REACT_APP_SUPABASE_ANON_KEY=your-anon-key'
    );
  }
}

const supabaseOptions = {
  auth: {
    storageKey: AUTH_STORAGE_KEY,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
    // PKCE, not the SDK default of 'implicit'. Implicit flow returns the session
    // in the URL *fragment* (`#access_token=...&refresh_token=...`), so every
    // sign-in put live credentials into browser history, screenshots, screen
    // shares and pasted console output. PKCE instead returns a single-use
    // authorization code as `?code=`, which the SDK exchanges for the session over
    // HTTPS. The code is not a credential: it is redeemable once, only by the
    // browser holding the matching verifier, and the SDK strips it from the URL via
    // history.replaceState. It does still reach CDN/edge access logs, so "no
    // long-lived token in a URL" is the accurate claim, not "nothing in a URL".
    // The code verifier lives in `storage` above under
    // `<storageKey>-code-verifier`, so it must be a store that survives the
    // OAuth round-trip (localStorage does; sessionStorage would too, but not a
    // memory store).
    flowType: 'pkce' as const,
  },
  global: {
    headers: {
      'x-application-name': 'umd-academic-planner',
    },
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
};

export const supabase: SupabaseClient = createClient(
  supabaseUrl || '',
  supabaseAnonKey || '',
  supabaseOptions
);

if (process.env.NODE_ENV === 'development') {
  console.log('Supabase client initialized:', {
    url: supabaseUrl,
    hasAnonKey: !!supabaseAnonKey,
    storageKey: supabaseOptions.auth.storageKey,
  });
}

// Lazy imports to avoid circular dependency with tokenManager and authErrorHandler.
const getTokenManager = () => import('./tokenManager');
const getAuthErrorHandler = () => import('../utils/authErrorHandler');

export interface SupabaseHelpers {
  getCurrentUser: () => Promise<User | null>;
  isAuthenticated: () => Promise<boolean>;
  getAccessToken: () => Promise<string | null>;
  getSession: () => Promise<Session | null>;
  clearAuthData: () => Promise<void>;
}

export const supabaseHelpers: SupabaseHelpers = {
  getCurrentUser: async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();

      if (error) {
        const { isPermanentRefreshError } = await getTokenManager();
        if (isPermanentRefreshError(error)) {
          const { handleAuthError } = await getAuthErrorHandler();
          handleAuthError();
          return null;
        }
      }

      return user;
    } catch (error) {
      console.error('Error getting current user:', error);

      const { isPermanentRefreshError } = await getTokenManager();
      if (isPermanentRefreshError(error as Error)) {
        const { handleAuthError } = await getAuthErrorHandler();
        handleAuthError();
      }

      return null;
    }
  },

  isAuthenticated: async () => {
    try {
      const { isAuthenticated } = await getTokenManager();
      if (isAuthenticated()) {
        return true;
      }
    } catch {
      // Fall through to async check
    }

    const user = await supabaseHelpers.getCurrentUser();
    return !!user;
  },

  getAccessToken: async () => {
    try {
      const { getValidAccessToken } = await getTokenManager();
      return await getValidAccessToken();
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  },

  getSession: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        const { isPermanentRefreshError } = await getTokenManager();
        if (isPermanentRefreshError(error)) {
          const { handleAuthError } = await getAuthErrorHandler();
          handleAuthError();
          return null;
        }
      }

      return session;
    } catch (error) {
      console.error('Error getting session:', error);

      const { isPermanentRefreshError } = await getTokenManager();
      if (isPermanentRefreshError(error as Error)) {
        const { handleAuthError } = await getAuthErrorHandler();
        handleAuthError();
      }

      return null;
    }
  },

  clearAuthData: async () => {
    const authKeys = [
      AUTH_STORAGE_KEY,
      'academic_planner_beta_auth',
      SUPABASE_AUTH_TOKEN_KEY,
      // PKCE code verifier. The SDK already removes it on signOut() and on a
      // failed exchange, and a stranded one is inert (a callback needs BOTH a
      // `?code=` and a verifier), but "clear all auth data" should be true.
      PKCE_CODE_VERIFIER_KEY,
    ];

    authKeys.forEach((key) => {
      localStorage.removeItem(key);
    });

    try {
      const { clearTokenCache } = await getTokenManager();
      clearTokenCache();
    } catch {
      // TokenManager may not be loaded yet
    }

    console.log('Cleared all auth data from localStorage');
  },
};

/**
 * Strip auth material left in the address bar by a redirect that did NOT complete.
 *
 * The SDK cleans the URL on success (`searchParams.delete('code')` for PKCE,
 * `location.hash = ''` for implicit), but on failure it throws first and the URL
 * keeps whatever it had. Two cases matter:
 *
 *  - A stale `?code=` — harmless (single-use, and unredeemable once the verifier
 *    is gone) but it persists across reloads and looks alarming.
 *  - A live `#access_token=…&refresh_token=…` — this is the deploy transition:
 *    a user mid-redirect when PKCE ships comes back with an implicit-shaped URL,
 *    the PKCE client refuses it, and the throw happens BEFORE the SDK's own
 *    `location.hash = ''`. The tokens are unusable to us but they are sitting in
 *    the address bar, which is the exact leak this whole change exists to close.
 *
 * Uses replaceState so no history entry is added.
 */
export const clearAuthParamsFromUrl = (): void => {
  try {
    const url = new URL(window.location.href);
    let changed = false;

    if (url.searchParams.has('code')) {
      url.searchParams.delete('code');
      changed = true;
    }
    // GoTrue's failure redirects carry these instead of a token — in the query for
    // PKCE and in the fragment for implicit. Leaving them behind defeats the point:
    // the URL still reads `?error=server_error&error_description=…` across reloads.
    for (const key of ['error', 'error_code', 'error_description']) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (/(^|[#&])(access_token|refresh_token|provider_token|error|error_code|error_description)=/.test(url.hash)) {
      url.hash = '';
      changed = true;
    }

    if (changed) {
      window.history.replaceState(window.history.state, '', url.toString());
    }
  } catch {
    // Non-browser or locked-down environment — nothing to clean.
  }
};

export default supabase;
